import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { deleteExamFromGroupOperation } from './delete-exam-from-group/operations/delete-exam-from-group-operation';
import { AddExamToGroupDialog } from './add-exam-to-group/add-exam-to-group-dialog';
import { observer } from 'mobx-react-lite';
import { ExternalExamGroupCrud } from '../../../../../../api/types/external-exams/external-exam-group-crud';
import dateUtils from '../../../../../../utils/date-format';
import { ExternalExamGroup } from '../../../../../../api/types/external-exams/external-exam-group';
import { EditExamInGroupDialog } from './edit-exam-in-group/edit-exam-in-group-dialog';
import { WarningDialog } from '../../../../../../component/ui/warning-dialog/warning-dialog';
import externalExamGroupsStore from '../../../../../../stores/external-exam-groups-store';

interface GroupDetailsDialogProps {
  group: ExternalExamGroup | null;
  visible: boolean;
  onHide: () => void;
  onGroupUpdate?: (updatedGroup: ExternalExamGroup) => void;
}

export const GroupDetailsDialog: React.FC<GroupDetailsDialogProps> = observer(
  ({ group, visible, onHide, onGroupUpdate }) => {
    const [editExam, setEditExam] = useState<ExternalExamGroupCrud | null>(
      null,
    );
    const [deleteExam, setDeleteExam] = useState<ExternalExamGroupCrud | null>(
      null,
    );
    const [deleteExamLoading, setDeleteExamLoading] = useState(false);
    const [showAddExam, setShowAddExam] = useState(false);

    if (!group) return null;

    return (
      <>
        <Dialog
          header={`פרטי מקבץ: ${group.groupName}`}
          visible={visible}
          onHide={onHide}
          style={{
            width: '90vw',
            maxWidth: '1200px',
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '8px',
          }}
          modal
        >
          <Button
            onClick={() => setShowAddExam(true)}
            style={{ height: '30px', marginBottom: '16px' }}
            label={'הוספת מבחן'}
            icon="pi pi-plus"
          />
          <DataTable
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו מבחנים במקבץ זה</div>
            }
            value={group.exams}
            dataKey="id"
            tableStyle={{
              direction: 'rtl',
              marginTop: '16px',
            }}
          >
            <Column
              style={{ textAlign: 'right' }}
              header="שם מבחן"
              body={(rowData: ExternalExamGroupCrud) => (
                <div>{rowData.abstractExternalExam.examName}</div>
              )}
            />
            <Column
              style={{ textAlign: 'right' }}
              header="סמל"
              body={(rowData: ExternalExamGroupCrud) => (
                <div>{rowData.abstractExternalExam.symbol}</div>
              )}
            />
            <Column
              style={{ textAlign: 'right' }}
              header="תאריך ושעת התחלה"
              body={(rowData: ExternalExamGroupCrud) => (
                <div>
                  {dateUtils.displayFormatDate(rowData.startAt)}{' '}
                  {new Date(rowData.startAt).toLocaleTimeString('he-IL', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              )}
            />
            <Column
              style={{ textAlign: 'right' }}
              header="משך (דקות)"
              body={(rowData: ExternalExamGroupCrud) => (
                <div>{rowData.durationMinutes}</div>
              )}
            />
            <Column
              style={{ textAlign: 'right' }}
              header="תאריך אחרון לרישום"
              body={(rowData: ExternalExamGroupCrud) => (
                <div>
                  {dateUtils.displayFormatDate(
                    rowData.studentRegistrationDueDate,
                  )}
                </div>
              )}
            />
            <Column
              style={{ textAlign: 'right' }}
              header="פעולות"
              body={(rowData: ExternalExamGroupCrud) => (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    icon="pi pi-pencil"
                    text
                    severity="warning"
                    tooltip={'עריכת מבחן'}
                    onClick={() => setEditExam(rowData)}
                  />

                  <Button
                    icon="pi pi-trash"
                    text
                    severity="danger"
                    tooltip={'מחיקת מבחן'}
                    onClick={() => setDeleteExam(rowData)}
                  />
                </div>
              )}
            />
          </DataTable>
        </Dialog>

        {editExam && (
          <EditExamInGroupDialog
            group={group}
            exam={editExam}
            visible={true}
            onHide={() => setEditExam(null)}
            onGroupUpdate={onGroupUpdate}
          />
        )}

        {deleteExam && (
          <WarningDialog
            header="אישור מחיקת מבחן"
            isVisible={deleteExam !== null}
            onHide={() => {
              if (!deleteExamLoading) {
                setDeleteExam(null);
              }
            }}
            addIcon={true}
            notInformation={'שים לב, תוכל להוסיף מבחן זה בהמשך'}
            content="האם אתה בטוח שברצונך למחוק את המבחן?"
            quoteName={deleteExam.abstractExternalExam.examName}
            smallLetters="פעולה זו אינה ניתנת לביטול"
            onConfirm={async () => {
              if (deleteExam) {
                await deleteExamFromGroupOperation(
                  group.id,
                  deleteExam.id,
                  (examId: number | null) => {
                    // Convert operation's loading pattern (examId | null) to boolean
                    setDeleteExamLoading(examId !== null);
                  },
                  () => {
                    setDeleteExam(null);
                    setDeleteExamLoading(false);
                    // Update the parent component's selectedGroup with the updated group from store
                    if (group && onGroupUpdate) {
                      const updatedGroup = externalExamGroupsStore.groups.find(
                        (g) => g.id === group.id,
                      );
                      if (updatedGroup) {
                        onGroupUpdate(updatedGroup);
                      }
                    }
                  },
                );
              }
            }}
            confirmLabel="מחק"
            confirmLoading={deleteExamLoading}
          />
        )}

        {showAddExam && (
          <AddExamToGroupDialog
            group={group}
            visible={true}
            onHide={() => setShowAddExam(false)}
            onGroupUpdate={onGroupUpdate}
          />
        )}
      </>
    );
  },
);
